// src/pages/autopilot/ListCreation.js
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  setListData,
  setSlackWebhook,
  setLoading,
  setError,
  goToPreviousStep,
  resetAutoPilot
} from '../../store/slices/autoPilotSlice';
import { createAutoPilotList, setupSlackIntegration } from '../../services/autoPilotService';
import SlackIntegration from '../../components/autopilot/SlackIntegration';

function ListCreation({ onBack }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    selectedProspects,
    loading,
    error,
    slackWebhook
  } = useSelector((state) => state.autoPilot);

  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [slackEnabled, setSlackEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(slackWebhook || '');
  const [listCreated, setListCreated] = useState(false);
  const [createdListId, setCreatedListId] = useState(null);

  const handleCreateList = async () => {
    if (!listName.trim()) {
      dispatch(setError('Please enter a list name'));
      return;
    }

    if (selectedProspects.length === 0) {
      dispatch(setError('No prospects selected'));
      return;
    }

    dispatch(setLoading(true));
    dispatch(setError(null));

    try {
      const listData = {
        name: listName.trim(),
        description: listDescription.trim()
      };

      const response = await createAutoPilotList(listData, selectedProspects);
      if (response.status === 'success' && response.data) {
        setListCreated(true);
        setCreatedListId(response.data._id || response.data.id);
        dispatch(setListData(response.data));

        // Setup Slack integration if enabled
        if (slackEnabled && webhookUrl.trim()) {
          try {
            await setupSlackIntegration(response.data._id || response.data.id, webhookUrl.trim());
            dispatch(setSlackWebhook(webhookUrl.trim()));
          } catch (slackError) {
            console.error('Slack integration failed:', slackError);
            // Don't fail the whole process if Slack setup fails
          }
        }

        // Reset after 3 seconds and redirect
        setTimeout(() => {
          dispatch(resetAutoPilot());
          navigate('/lists');
        }, 3000);
      } else {
        dispatch(setError(response.message || 'Failed to create list. Please try again.'));
      }
    } catch (err) {
      dispatch(setError(err.response?.data?.message || 'An error occurred. Please try again.'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      dispatch(goToPreviousStep());
    }
  };

  const handleViewList = () => {
    if (createdListId) {
      navigate(`/list/${createdListId}`);
    } else {
      navigate('/lists');
    }
  };

  if (listCreated) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 3, textAlign: 'center' }}>
        <Card sx={{ boxShadow: 3, p: 4 }}>
          <CardContent>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#1BE885', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
              List Created Successfully!
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
              Your list "<strong>{listName}</strong>" has been created with <strong>{selectedProspects.length}</strong> prospects.
              {slackEnabled && ' Slack notifications have been configured.'}
            </Typography>
            <Button
              variant="contained"
              onClick={handleViewList}
              sx={{
                bgcolor: '#1BE885',
                color: '#000',
                fontWeight: 600,
                '&:hover': { bgcolor: '#20ec84' }
              }}
            >
              View List
            </Button>
            <Typography variant="body2" sx={{ color: '#888', mt: 2 }}>
              Redirecting to lists page...
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Create Your List
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
        Provide a name and description for your list. You can optionally configure Slack integration to receive notifications.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(setError(null))}>
          {error}
        </Alert>
      )}

      <Card sx={{ boxShadow: 3, mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            label="List Name"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            placeholder="e.g., VP Sales - Walmart Bay Area"
            variant="outlined"
            required
            disabled={loading}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description (Optional)"
            value={listDescription}
            onChange={(e) => setListDescription(e.target.value)}
            placeholder="Add a description for this list"
            variant="outlined"
            multiline
            rows={3}
            disabled={loading}
            sx={{ mb: 3 }}
          />

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>{selectedProspects.length}</strong> prospect{selectedProspects.length !== 1 ? 's' : ''} will be added to this list
            </Typography>
          </Alert>

          <SlackIntegration
            webhook={webhookUrl}
            onWebhookChange={setWebhookUrl}
            enabled={slackEnabled}
            onEnabledChange={setSlackEnabled}
          />
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          disabled={loading}
          sx={{
            borderColor: '#666',
            color: '#666',
            '&:hover': { borderColor: '#333', bgcolor: '#f5f5f5' }
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateList}
          disabled={loading || !listName.trim() || selectedProspects.length === 0}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          sx={{
            bgcolor: '#1BE885',
            color: '#000',
            fontWeight: 600,
            '&:hover': { bgcolor: '#20ec84' },
            '&:disabled': { bgcolor: '#ccc' }
          }}
        >
          {loading ? 'Creating List...' : 'Create List'}
        </Button>
      </Box>
    </Box>
  );
}

export default ListCreation;
